import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import api from '../utils/api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

// Phone number normalization helper (client-side)
const normalizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') return '';
  
  // Remove all whitespace and hyphens
  let normalized = phone.replace(/[\s\-]/g, '');
  
  // Handle various country code formats
  if (normalized.startsWith('+91')) {
    normalized = normalized.substring(3);
  } else if (normalized.startsWith('0091')) {
    normalized = normalized.substring(4);
  } else if (normalized.startsWith('91') && normalized.length > 10) {
    normalized = normalized.substring(2);
  }
  
  // Return only the last 10 digits
  return normalized.slice(-10);
};

// Format phone for display
const formatPhoneForDisplay = (phone) => {
  const normalized = normalizePhoneNumber(phone);
  if (!normalized || normalized.length !== 10) return phone;
  return `+91-${normalized}`;
};

const AdminAccessManagement = () => {
  // Form states for creating/editing access
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Data states
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState('All');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState('All');

  // UI states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userFetched, setUserFetched] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);

  // Fetch unique roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get('/api/admin-access/roles');
        const data = await response.json();
        if (data.success) {
          setRoles(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      }
    };
    fetchRoles();
  }, []);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (selectedRole !== 'All') {
          params.append('role', selectedRole);
        }
        if (selectedActiveStatus !== 'All') {
          params.append('isActive', selectedActiveStatus === 'Active');
        }

        const response = await api.get(`/api/admin-access/users?${params}`);
        const data = await response.json();
        if (data.success) {
          setUsers(data.data);
          setFilteredUsers(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
        setMessage({ type: 'error', text: 'Failed to fetch users' });
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [selectedRole, selectedActiveStatus]);

  // Fetch user by phone number
  const handlePhoneChange = async (value) => {
    setPhone(value);
    setUserFetched(false);
    setIsEditing(false);
    setFirstName('');
    setLastName('');
    setRole('');
    setEditingUserId(null);

    // Normalize the phone for API call
    const normalized = normalizePhoneNumber(value);
    
    if (normalized.length === 10) {
      try {
        const response = await api.get(`/api/admin-access/user-by-phone?phone=${normalized}`);
        const data = await response.json();

        if (data.success) {
          setFirstName(data.data.firstName);
          setLastName(data.data.lastName);
          setRole(data.data.role);
          setIsEditing(true);
          setEditingUserId(data.data._id);
          setUserFetched(true);
          setMessage({ type: 'info', text: 'User found. You can now update the details.' });
        } else {
          setUserFetched(false);
          setMessage({ type: 'info', text: 'User not found. You can create new access.' });
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        setUserFetched(false);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!phone || !firstName || !lastName || !role) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    try {
      setSubmitting(true);

      // Normalize phone before sending
      const normalizedPhone = normalizePhoneNumber(phone);

      let response;

      if (isEditing && editingUserId) {
        // Update existing user
        response = await api.put(`/api/admin-access/update/${editingUserId}`, {
          firstName,
          lastName,
          role,
          isActive
        });
      } else {
        // Create new user
        response = await api.post('/api/admin-access/create', {
          phone: normalizedPhone,
          firstName,
          lastName,
          role,
          email: email || undefined
        });
      }

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        // Reset form
        setPhone('');
        setFirstName('');
        setLastName('');
        setRole('');
        setEmail('');
        setIsActive(true);
        setIsEditing(false);
        setEditingUserId(null);
        setUserFetched(false);
        // Refresh users list
        const refreshResponse = await api.get('/api/admin-access/users');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setUsers(refreshData.data);
          setFilteredUsers(refreshData.data);
        }
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Failed to submit:', error);
      setMessage({ type: 'error', text: 'Failed to process request' });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit user from table
  const handleEditUser = (user) => {
    setPhone(user.phone);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setRole(user.role);
    setIsActive(user.isActive);
    setIsEditing(true);
    setEditingUserId(user._id);
    setUserFetched(true);
  };

  // Handle delete user
  const handleDeleteConfirm = (userId) => {
    setDeleteUserId(userId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteUser = async () => {
    try {
      setSubmitting(true);
      const response = await api.delete(`/api/admin-access/delete/${deleteUserId}`);

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'User deleted successfully' });
        setDeleteConfirmOpen(false);
        setDeleteUserId(null);
        // Refresh users list
        const refreshResponse = await api.get('/api/admin-access/users');
        const refreshData = await refreshResponse.json();
        if (refreshData.success) {
          setUsers(refreshData.data);
          setFilteredUsers(refreshData.data);
        }
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (error) {
      console.error('Failed to delete user:', error);
      setMessage({ type: 'error', text: 'Failed to delete user' });
    } finally {
      setSubmitting(false);
    }
  };

  // Clear form
  const handleClearForm = () => {
    setPhone('');
    setFirstName('');
    setLastName('');
    setRole('');
    setEmail('');
    setIsActive(true);
    setIsEditing(false);
    setEditingUserId(null);
    setUserFetched(false);
    setMessage({ type: '', text: '' });
  };

  const getRoleColor = (role) => {
    const colors = {
      'Admin': 'error',
      'SuperUser': 'warning',
      'PathashalaAdmin': 'primary',
      'Parikshaadhikari': 'info',
      'Vatuvu': 'success',
      'Anonymous': 'default'
    };
    return colors[role] || 'default';
  };

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <h1 style={{ color: '#7f4b2d', marginBottom: '8px' }}>🔐 Admin Access Management</h1>
        <p style={{ color: '#666', margin: 0 }}>
          Create new user access or manage existing admin permissions
        </p>
      </Box>

      {/* Messages */}
      {message.text && (
        <Alert
          severity={message.type}
          onClose={() => setMessage({ type: '', text: '' })}
          sx={{ mb: 3 }}
        >
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Access Form */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ color: '#7f4b2d' }} />
                  {isEditing ? 'Update User Access' : 'Create New Access'}
                </Box>
              }
              sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}
            />
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'grid', gap: 2 }}>
                  {/* Mobile Number */}
                  <TextField
                    label="Mobile Number *"
                    type="tel"
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    placeholder="Enter 10 digits or +91-xxxxxxxxxx"
                    helperText={phone ? `Normalized: ${formatPhoneForDisplay(phone)}` : 'Accepts formats: 10 digits, +91, 0091, with/without hyphens'}
                    disabled={isEditing}
                    fullWidth
                  />

                  {/* First Name */}
                  <TextField
                    label="First Name *"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    fullWidth
                  />

                  {/* Last Name */}
                  <TextField
                    label="Last Name *"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    fullWidth
                  />

                  {/* Role Dropdown */}
                  <FormControl fullWidth>
                    <InputLabel>Role *</InputLabel>
                    <Select
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      label="Role *"
                    >
                      {roles.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Email (Optional) */}
                  <TextField
                    label="Email (Optional)"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                  />

                  {/* Active Status - Only show when editing */}
                  {isEditing && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                        />
                      }
                      label="Active Status"
                    />
                  )}

                  {/* Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={submitting || !phone || !firstName || !lastName || !role}
                      sx={{ backgroundColor: '#7f4b2d', flex: 1 }}
                    >
                      {submitting ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          {isEditing ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        isEditing ? 'Update Access' : 'Create Access'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleClearForm}
                      sx={{ flex: 1 }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>
              </form>

              {/* Info Box */}
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#f0f7ff',
                  borderLeft: '4px solid #7f4b2d',
                  borderRadius: '4px'
                }}
              >
                <p style={{ margin: 0, fontSize: '14px', color: '#333' }}>
                  ℹ️ <strong>How to use:</strong>
                </p>
                <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '13px', color: '#666' }}>
                  <li>Enter mobile number in any format: 10 digits, +91-xxxxxxxxxx, 0091xxxxxxxxxx, etc.</li>
                  <li>Phone numbers are automatically normalized to standard format</li>
                  <li>The system searches for users across all phone formats in database</li>
                  <li>If user exists, you can update their details</li>
                  <li>If user doesn't exist, create a new access</li>
                  <li>Select the appropriate role from the dropdown</li>
                </ul>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Users Filter and Actions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              title="Filters"
              sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}
            />
            <CardContent>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {/* Role Filter */}
                <FormControl fullWidth>
                  <InputLabel>Filter by Role</InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    label="Filter by Role"
                  >
                    <MenuItem value="All">All Roles</MenuItem>
                    {roles.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Active Status Filter */}
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={selectedActiveStatus}
                    onChange={(e) => setSelectedActiveStatus(e.target.value)}
                    label="Filter by Status"
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>

                {/* Statistics */}
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>
                    📊 Summary
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                    Total Users: <strong>{users.length}</strong>
                  </p>
                  <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>
                    Filtered Users: <strong>{filteredUsers.length}</strong>
                  </p>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Users Table */}
      <Card sx={{ mt: 3 }}>
        <CardHeader
          title="All Users"
          sx={{ backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' }}
        />
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#7f4b2d' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Role</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 3, color: '#999' }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user) => (
                      <TableRow key={user._id} hover>
                        <TableCell>
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>
                          {formatPhoneForDisplay(user.phone)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.role}
                            color={getRoleColor(user.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            color={user.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditUser(user)}
                              sx={{ color: '#7f4b2d' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteConfirm(user._id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this user? This action cannot be undone.</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAccessManagement;
